import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { DIRECT_DIRECTORY } from "../world/districts";

interface HomeDirectDirectoryProps {
  open: boolean;
  onClose: () => void;
}

const GROUPS = [
  {
    title: "Explore",
    paths: ["/market", "/library", "/services", "/academy", "/insights", "/ai"],
  },
  {
    title: "Personal",
    paths: ["/inbox", "/profile", "/cart", "/learning", "/bookings", "/certificate"],
  },
] as const;

export function HomeDirectDirectory({ open, onClose }: HomeDirectDirectoryProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const grouped = useMemo(
    () =>
      GROUPS.map((group) => ({
        ...group,
        entries: group.paths
          .map((path) => DIRECT_DIRECTORY.find((entry) => entry.path === path))
          .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry)),
      })),
    [],
  );

  useEffect(() => {
    const node = dialog.current;
    if (!node) return;
    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  return (
    <dialog
      ref={dialog}
      className="home-directory-dialog"
      aria-labelledby="home-directory-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="home-directory-panel">
        <header>
          <div>
            <span>Direct access</span>
            <h2 id="home-directory-title">Browse the city without the world.</h2>
          </div>
          <button type="button" aria-label="Close direct directory" onClick={onClose}>
            <X aria-hidden="true" />
          </button>
        </header>
        <p>
          Every essential route remains available in Static mode, by keyboard and by direct URL.
        </p>
        <div className="home-directory-groups">
          {grouped.map((group) => {
            const sectionId = "directory-" + group.title.toLowerCase();
            return (
              <section key={group.title} aria-labelledby={sectionId}>
                <h3 id={sectionId}>{group.title}</h3>
                <div>
                  {group.entries.map((entry) => (
                    <Link key={entry.path} to={entry.path} onClick={onClose}>
                      <span>{entry.label}</span>
                      <b aria-hidden="true">↗</b>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </dialog>
  );
}
